import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { MediaType } from "@/types";
import { categories } from "@/mocks/site";
import { getMediaByType } from "@/lib/data";
import { useSeo } from "@/hooks/useSeo";
import MediaCard from "@/components/feature/MediaCard";
import FilterPanel from "./components/FilterPanel";

const PAGE_SIZE = 8;

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const mediaType = (type as MediaType) || "movie";
  const cat = categories.find((c) => c.type === mediaType) || categories[0];

  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useSeo(
    `榴莲视频 - ${cat.name}频道`,
    `${cat.intro.slice(0, 60)}——榴莲视频${cat.name}频道，涵盖${cat.name}的分类推荐、多维筛选与热门榜单。`,
    `榴莲视频,${cat.name},${cat.englishName},影视${cat.name},在线观看`,
  );

  const toggle = <T,>(list: T[], value: T, setter: (v: T[]) => void) => {
    setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
    setPage(1);
  };

  const reset = () => {
    setSelectedGenres([]);
    setSelectedRegions([]);
    setSelectedYears([]);
    setSort("latest");
    setPage(1);
  };

  const filtered = useMemo(() => {
    let list = getMediaByType(mediaType);
    if (selectedGenres.length) list = list.filter((m) => m.genres.some((g) => selectedGenres.includes(g)));
    if (selectedRegions.length) list = list.filter((m) => selectedRegions.includes(m.region));
    if (selectedYears.length) list = list.filter((m) => selectedYears.includes(m.year));
    const sorted = [...list];
    if (sort === "latest") sorted.sort((a, b) => b.year - a.year || b.popularity - a.popularity);
    if (sort === "hot") sorted.sort((a, b) => b.popularity - a.popularity);
    if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    return sorted;
  }, [mediaType, selectedGenres, selectedRegions, selectedYears, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const shown = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const filterPanel = (
    <FilterPanel
      type={mediaType}
      selectedGenres={selectedGenres}
      selectedRegions={selectedRegions}
      selectedYears={selectedYears}
      sort={sort}
      onToggleGenre={(v) => toggle(selectedGenres, v, setSelectedGenres)}
      onToggleRegion={(v) => toggle(selectedRegions, v, setSelectedRegions)}
      onToggleYear={(v) => toggle(selectedYears, v, setSelectedYears)}
      onSort={(v) => {
        setSort(v);
        setPage(1);
      }}
      onReset={reset}
    />
  );

  return (
    <>
      <section className="relative h-[260px] w-full overflow-hidden md:h-[320px]">
        <img
          src={cat.cover}
          alt={`${cat.name}频道`}
          title={`${cat.name}频道-榴莲视频`}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-50/30"></div>
        <div className="absolute inset-0 mx-auto flex max-w-[1280px] flex-col justify-end px-4 pb-8 md:px-8">
          <nav className="mb-3 flex items-center gap-2 text-xs text-foreground-400">
            <Link to="/" className="hover:text-accent-500 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">{cat.name}频道</span>
          </nav>
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary-500 text-background-50 md:h-16 md:w-16">
              <i className={`${cat.icon} text-3xl`}></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                {cat.name}频道
              </h1>
              <p className="mt-1 text-sm text-foreground-400">{cat.slogan}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1280px] px-4 pb-16 md:px-8">
        <p className="max-w-3xl py-6 text-sm leading-relaxed text-foreground-400 md:text-base">
          {cat.intro}
        </p>

        <div className="mb-4 md:hidden">
          <button
            type="button"
            onClick={() => setMobileFilterOpen((v) => !v)}
            className="flex w-full items-center justify-between rounded-lg border border-background-200/60 bg-background-100/50 px-4 py-3 text-sm text-foreground-700 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line text-accent-500"></i>
              筛选与排序
            </span>
            <i className={mobileFilterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
          </button>
        </div>

        <div className={`${mobileFilterOpen ? "block" : "hidden"} mb-6 md:block`}>{filterPanel}</div>

        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-sm text-foreground-300">
          <span>
            共找到 <span className="text-accent-500">{filtered.length}</span> 部{cat.name}
          </span>
          <span>第 {page} / {totalPages} 页</span>
        </div>

        {shown.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 lg:gap-6">
            {shown.map((item, i) => (
              <div key={item.id} className={`animate-fade-up delay-${(i % 4) + 1}`}>
                <MediaCard item={item} />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-background-200/60 bg-background-100/40 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200/70 text-foreground-300">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </span>
            <p className="mt-4 font-heading text-lg text-foreground-950">未找到相关影视作品</p>
            <p className="mt-1 text-sm text-foreground-400">试试减少筛选条件，或重置后重新浏览</p>
            <button
              type="button"
              onClick={reset}
              className="mt-5 flex items-center gap-2 rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选条件
            </button>
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className={`flex h-9 items-center gap-1 rounded-md px-3 text-sm transition-colors whitespace-nowrap ${
                page === 1
                  ? "cursor-not-allowed bg-background-100 text-foreground-200"
                  : "cursor-pointer bg-background-100 text-foreground-500 hover:bg-background-200"
              }`}
            >
              <i className="ri-arrow-left-s-line"></i>上一页
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`h-9 w-9 rounded-md text-sm transition-colors cursor-pointer ${
                  p === page
                    ? "bg-accent-500 text-background-50"
                    : "bg-background-100 text-foreground-500 hover:bg-background-200"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className={`flex h-9 items-center gap-1 rounded-md px-3 text-sm transition-colors whitespace-nowrap ${
                page === totalPages
                  ? "cursor-not-allowed bg-background-100 text-foreground-200"
                  : "cursor-pointer bg-background-100 text-foreground-500 hover:bg-background-200"
              }`}
            >
              下一页<i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}
      </div>
    </>
  );
}